import { M } from '../palette';
import { blocks, MapBuilder, planks, symPoint, symYaw, yawToward, type Brush } from './builder';
import { emptyMeta, type MapDef, type MapMeta } from './types';

const S = 320;
const G = 32;
const SYM = 'rotateZ' as const;
/** Arena floor bounds (inside the arena wall). */
const A0 = 30;
const A1 = 290;
/** Arena wall height. */
const WALL = 22;

/** Box centered on the map's x midline (symmetric under the half-turn). */
const midBox = (b: MapBuilder, half: number, y0: number, z0: number, y1: number, z1: number, br: Brush): void => b.box(160 - half, y0, z0, 160 + half, y1, z1, br);

function brazier(b: MapBuilder, x: number, y: number, z: number): void {
  b.box(x - 3, y, z - 3, x + 3, y + 2, z + 3, M('steel_dark_1'));
  b.box(x - 2, y + 2, z - 2, x + 2, y + 5, z + 2, M('neon_orange'));
  b.box(x - 1, y + 5, z - 1, x + 1, y + 7, z + 1, M('neon_yellow'));
}

/** North half of the arena (z < 160); the half-turn copy completes it. */
function northHalf(b: MapBuilder): void {
  const sand = M('sand_2');
  const stone: Brush = blocks([M('sandstone_1')], 3, 0);
  const stone2: Brush = blocks([M('sandstone_2')], 3, 1);
  const trim: Brush = blocks([M('stone_2')], 3, 2);
  // Arena floor with a paved ring.
  b.box(A0, G - 1, A0, A1, G, 160, sand);
  b.box(A0, G - 1, A0, A1, G, A0 + 14, blocks([M('sandstone_3')], 3, 3));
  b.box(A0, G - 1, A0, A0 + 14, G, 160, blocks([M('sandstone_3')], 3, 3));
  b.box(A1 - 14, G - 1, A0, A1, G, 160, blocks([M('sandstone_3')], 3, 3));
  // Arena wall and tiered stands rising to the map edge.
  b.box(0, G, 0, S, G + WALL, A0, stone);
  b.box(0, G, 0, A0, G + WALL, 160, stone);
  b.box(A1, G, 0, S, G + WALL, 160, stone);
  for (let k = 0; k < 4; k++) {
    const y = G + WALL + k * 5;
    const inset = A0 - 6 - k * 6;
    b.box(0, y, 0, S, y + 5, inset, k % 2 ? stone2 : stone);
    b.box(0, y, 0, inset, y + 5, 160, k % 2 ? stone2 : stone);
    b.box(S - inset, y, 0, S, y + 5, 160, k % 2 ? stone2 : stone);
  }
  // Rail along the arena wall top.
  b.box(A0 - 2, G + WALL, A0 - 2, A1 + 2, G + WALL + 1, A0 - 1, trim);
  b.box(A0 - 2, G + WALL, A0 - 1, A0 - 1, G + WALL + 1, 160, trim);
  b.box(A1 + 1, G + WALL, A0 - 1, A1 + 2, G + WALL + 1, 160, trim);

  // Spawn gate: an arched alcove in the north wall with a raised portcullis and a banner.
  // The vestibule is open to the sky: the stands are cut away above it.
  b.clear(130, G, 8, 190, G + 60, A0);
  b.box(130, G - 1, 8, 190, G, A0, blocks([M('sandstone_3')], 3, 4));
  b.box(126, G + 26, A0 - 4, 194, G + 30, A0, trim);
  b.box(126, G, 8, 130, G + 42, A0, stone2);
  b.box(190, G, 8, 194, G + 42, A0, stone2);
  for (let x = 132; x < 190; x += 4) b.box(x, G + 20, A0 - 2, x + 1, G + 26, A0 - 1, M('steel_dark_1'));
  b.box(130, G + 22, A0 - 2, 190, G + 23, A0 - 1, M('steel_dark_1'));
  midBox(b, 14, G + 4, 6, G + 22, 8, M('team_red'));
  brazier(b, 124, G + WALL + 1, A0 - 6);
  brazier(b, 196, G + WALL + 1, A0 - 6);
  brazier(b, A0 - 6, G + WALL + 1, 90);

  // Cover: a shield wall in front of the gate, pillars, crate stacks and a low trench.
  b.box(124, G, 56, 196, G + 18, 62, stone2);
  b.box(124, G + 18, 55, 196, G + 20, 63, trim);
  for (const [x, z] of [[84, 84], [226, 84]]) {
    b.box(x - 6, G, z - 6, x + 6, G + 40, z + 6, stone);
    b.box(x - 8, G + 40, z - 8, x + 8, G + 44, z + 8, trim);
    b.box(x - 8, G, z - 8, x + 8, G + 4, z + 8, trim);
  }
  b.crate(96, G, 104);
  b.crate(96, G + 10, 106, 8);
  b.crate(106, G, 112, 8);
  b.crate(242, G, 128);
  b.box(96, G, 136, 120, G + 10, 142, planks([M('planks_dark_1')], 0));
  b.box(250, G, 60, 272, G + 14, 66, stone2);
  b.box(266, G, 40, 272, G + 14, 66, stone2);
  // Sunken sand trench along the west side, crossing the middle line.
  b.clear(A0 + 16, G - 6, 118, A0 + 46, G, 160);
  b.box(A0 + 16, G - 7, 118, A0 + 46, G - 6, 160, sand);
  b.climb(A0 + 16, G - 6, 123, 'n', 30, 6, sand);
  // Central dais (north half) with steps up from the north.
  midBox(b, 34, G, 132, G + 8, 160, stone2);
  midBox(b, 26, G + 8, 140, G + 9, 160, trim);
  b.climb(150, G, 124, 's', 20, 8, stone);
  // Banners on the arena wall.
  for (const x of [60, 250]) b.box(x, G + 4, A0 - 1, x + 12, G + 20, A0, M('cloth_red'));
  // A cheering crowd of blocky spectators on the tiers, and flags along the top.
  const shirts = [M('paint_red'), M('paint_blue'), M('paint_yellow'), M('paint_green'), M('paint_purple'), M('paint_orange'), M('paint_teal'), M('paint_pink')];
  const faces = [M('plaster_1'), M('paint_brown'), M('clay'), M('sand_3')];
  const fan = (x: number, y: number, z: number, i: number): void => {
    b.box(x, y, z, x + 3, y + 5, z + 2, shirts[i % shirts.length]);
    b.box(x, y + 5, z, x + 3, y + 8, z + 2, faces[(i * 7) % faces.length]);
    if (i % 3 === 0) b.box(x - 1, y + 4, z, x, y + 8, z + 1, shirts[(i + 3) % shirts.length]);
  };
  let n = 0;
  for (let k = 0; k < 3; k++) {
    const y = G + WALL + k * 5 + 5;
    const zRow = A0 - 12 - k * 6;
    for (let x = 16 + ((k * 5) % 9); x < S - 16; x += 9 + ((x * 3 + k) % 5)) {
      if (x > 120 && x < 200) continue;
      fan(x, y, zRow, n++);
    }
    const xRow = A0 - 12 - k * 6;
    for (let z = 40 + ((k * 7) % 9); z < 150; z += 10 + ((z + k) % 4)) {
      fan(xRow, y, z, n++);
      fan(S - xRow - 3, y, z, n++);
    }
  }
  for (const x of [20, 100, 220, 300]) {
    b.box(x, G + WALL + 20, 2, x + 1, G + WALL + 46, 3, M('log_2'));
    b.box(x + 1, G + WALL + 38, 2, x + 12, G + WALL + 45, 3, x < 160 ? M('cloth_yellow') : M('cloth_white'));
  }
}

function build(b: MapBuilder): MapMeta {
  b.ground(G, M('sand_2'), M('sand_1'), M('rock_sand'));
  northHalf(b);
  b.symmetrize(SYM);

  const gy = G / 10;
  const meta = emptyMeta(gy, 16, 16);
  const team0: [number, number][] = [[16, 1.6], [14, 1.6], [18, 1.6], [16, 2.5]];
  for (const [x, z] of team0) {
    const yaw = Math.PI;
    meta.spawns.push({ team: 0, x, y: gy, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  const ffaHalf: [number, number][] = [[5, 5], [24, 4.5], [4.5, 10], [27.5, 13], [11.5, 9.5], [20.5, 9.5], [16, 7.5], [6, 15]];
  for (const [x, z] of ffaHalf) {
    meta.spawns.push({ team: -1, x, y: b.standAt(x, z, 4), z, yaw: yawToward(x, z, 16, 16) });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: -1, x: mx, y: b.standAt(mx, mz, 4), z: mz, yaw: yawToward(mx, mz, 16, 16) });
  }
  meta.points.push({ id: 'A', name: 'North Gate', x: 16, y: gy, z: 9.4, r: 3 });
  meta.points.push({ id: 'B', name: 'Dais', x: 16, y: (G + 9) / 10, z: 16, r: 3 });
  meta.points.push({ id: 'C', name: 'South Gate', x: 16, y: gy, z: 22.6, r: 3 });
  meta.flags.push({ team: 0, x: 16, y: gy, z: 2 }, { team: 1, x: 16, y: gy, z: 30 });
  meta.pickups.push({ kind: 'health', x: 4.5, y: gy, z: 16 }, { kind: 'health', x: 27.5, y: gy, z: 16 }, { kind: 'armor', x: 16, y: (G + 9) / 10, z: 16 });
  return meta;
}

export const duelPits: MapDef = {
  id: 'duel_pits',
  name: 'Duel Pits',
  description: 'A sun-baked arena for one-on-one showdowns: two gates, one dais, nowhere to hide for long.',
  size: [S, 128, S],
  modes: ['duel', 'ffa', 'sandbox', 'practice'],
  players: [2, 8],
  seed: 1122,
  version: 2,
  theme: {
    skyTop: 0x3d4f96,
    skyHorizon: 0xf2ae70,
    fog: 0xe0a47a,
    fogNear: 40,
    fogFar: 120,
    sun: [0.6, 0.38, 0.3],
    sunColor: 0xffc080,
    sunIntensity: 0.95,
    ambient: 0x9ca4d0,
    ambientIntensity: 0.52,
    outside: 0xc8a068,
    music: 'castle',
  },
  build,
};
